import {parseSectionId, type SectionId} from '@cip/core/section';
import {Role} from 'types/role';
import {type CourseUser, ProfileContextData} from './ProfileContext';

// Role hierarchy + conversion helpers now live in @cip/core so the webapp and
// cloud functions share a single definition. Re-exported here so existing
// `contexts/profileUtil` import sites keep working unchanged.
export {
  firebaseStringToRole,
  isMinimumRole,
  roleToAdminString,
  roleToDisplayString,
  roleToFirebaseString,
  roleToForumString,
} from '@cip/core/firebase';

export const isStaffRole = (courseRole: Role): boolean => {
  return [Role.ADMIN, Role.INSTRUCTOR, Role.TA].includes(courseRole);
};

export const isHeadTa = (courseRole: Role): boolean => {
  return courseRole === Role.TA;
};

export const isInCourse = (courseRole: Role): boolean => {
  return courseRole !== Role.UNREGISTERED;
};

export const isATypeOfStudent = (courseRole: Role): boolean => {
  return courseRole === Role.STUDENT || courseRole === Role.EXPERIENCED_STUDENT;
};

// Senior SLs have every section-leader permission, so they count as section
// leaders everywhere this predicate gates SL access. (Review access, which
// senior SLs additionally hold, is gated separately via isMinimumRole.)
export const isSectionLeader = (courseRole: Role): boolean => {
  return (
    courseRole === Role.SECTION_LEADER || courseRole === Role.SENIOR_SL
  );
};

export const isMentor = (courseRole: Role): boolean => {
  return courseRole === Role.MENTOR;
};

export const isExperiencedStudent = (courseRole: Role): boolean => {
  return courseRole === Role.EXPERIENCED_STUDENT;
};

export const isStudent = (courseRole: Role): boolean => {
  return courseRole === Role.STUDENT;
};

export const isInstructor = (courseRole: Role): boolean => {
  return courseRole === Role.INSTRUCTOR;
};

export const isAdmin = (courseRole: Role): boolean => {
  return courseRole === Role.ADMIN;
};

export const isProfileComplete = (
  profileContext: ProfileContextData,
): boolean => {
  const userData = profileContext.userData;
  const userProtectedData = profileContext.userProtectedData;

  const hasDisplayName =
    userData.displayName &&
    userData.displayName.length > 0 &&
    userData.displayName !== 'Anon';
  const hasCountry = userData.country && userData.country.length > 0;
  const hasDob = userProtectedData?.dateOfBirth !== undefined;

  const hasFields = hasDisplayName && hasCountry && hasDob;
  if (!hasFields) {
    return false;
  }

  const requiredDobFields = ['day', 'month', 'year'];
  const dobFields = userProtectedData.dateOfBirth;
  const hasDobFields = requiredDobFields.every(
    field => dobFields[field] !== undefined,
  );

  return hasDobFields;
};

/**
 * Returns the id of the viewer's own (assigned) section in the current
 * course: the first entry of `courseSections`.
 *
 * @param userData - The course user from `ProfileContext`; may be missing
 *   while the profile loads.
 * @return The own section id, or `undefined` when the user has no section in
 *   this course or the profile has not loaded.
 * @throws Never.
 */
export function getOwnSectionId(
  userData: Pick<CourseUser, 'courseSections'> | null | undefined,
): SectionId | undefined {
  return parseSectionId(userData?.courseSections?.[0]?.id);
}
